import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Camera, ZoomIn, ZoomOut, RotateCw, Save, Move } from 'lucide-react';

const CANVAS_SIZE = 300;   // visible crop area
const OUTPUT_SIZE = 512;   // exported avatar size

const AvatarCropModal = ({ open, file, onClose, onCropComplete, uploading = false }) => {
  const [imageSrc, setImageSrc] = useState(null);
  const [scale, setScale] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [rotation, setRotation] = useState(0);
  const [imgSize, setImgSize] = useState({ w: 0, h: 0 });
  const imageRef = useRef(null);
  const dragState = useRef({ dragging: false, startX: 0, startY: 0, origX: 0, origY: 0 });

  useEffect(() => {
    if (!file) { setImageSrc(null); return; }
    const reader = new FileReader();
    reader.onload = (e) => setImageSrc(e.target.result);
    reader.readAsDataURL(file);
    setScale(1); setPos({ x: 0, y: 0 }); setRotation(0);
  }, [file]);

  const onImgLoad = (e) => {
    const w = e.target.naturalWidth;
    const h = e.target.naturalHeight;
    setImgSize({ w, h });
    // Fit-to-cover: scale so shorter side matches canvas
    const cover = Math.max(CANVAS_SIZE / w, CANVAS_SIZE / h);
    setScale(cover);
    setPos({ x: 0, y: 0 });
  };

  const onMouseDown = (e) => {
    e.preventDefault();
    const point = 'touches' in e ? e.touches[0] : e;
    dragState.current = { dragging: true, startX: point.clientX, startY: point.clientY, origX: pos.x, origY: pos.y };
  };
  const onMouseMove = useCallback((e) => {
    if (!dragState.current.dragging) return;
    const point = 'touches' in e ? e.touches[0] : e;
    const dx = point.clientX - dragState.current.startX;
    const dy = point.clientY - dragState.current.startY;
    setPos({ x: dragState.current.origX + dx, y: dragState.current.origY + dy });
  }, []);
  const onMouseUp = () => { dragState.current.dragging = false; };

  useEffect(() => {
    if (!open) return;
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    window.addEventListener('touchmove', onMouseMove, { passive: false });
    window.addEventListener('touchend', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      window.removeEventListener('touchmove', onMouseMove);
      window.removeEventListener('touchend', onMouseUp);
    };
  }, [open, onMouseMove]);

  const zoom = (delta) => setScale((s) => Math.max(0.2, Math.min(5, s + delta)));

  const doCrop = async () => {
    if (!imageRef.current) return;
    const canvas = document.createElement('canvas');
    canvas.width = OUTPUT_SIZE;
    canvas.height = OUTPUT_SIZE;
    const ctx = canvas.getContext('2d');
    // black background so PNG has no transparency artifacts
    ctx.fillStyle = '#0A0A12';
    ctx.fillRect(0, 0, OUTPUT_SIZE, OUTPUT_SIZE);

    const ratio = OUTPUT_SIZE / CANVAS_SIZE;
    // Translate to center
    ctx.translate(OUTPUT_SIZE / 2, OUTPUT_SIZE / 2);
    if (rotation) ctx.rotate((rotation * Math.PI) / 180);
    // pos.x/pos.y are relative to canvas center; multiply by ratio
    ctx.translate(pos.x * ratio, pos.y * ratio);
    // draw with scale
    const drawW = imgSize.w * scale * ratio;
    const drawH = imgSize.h * scale * ratio;
    ctx.drawImage(imageRef.current, -drawW / 2, -drawH / 2, drawW, drawH);

    canvas.toBlob((blob) => {
      if (!blob) return;
      const cropped = new File([blob], (file?.name || 'avatar').replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg' });
      onCropComplete(cropped);
    }, 'image/jpeg', 0.92);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#0A0A12] border-white/10 text-white max-w-md p-6" data-testid="avatar-crop-modal">
        <DialogTitle className="text-white text-base font-medium flex items-center gap-2 mb-1">
          <Camera className="w-4 h-4 text-[#00E5FF]" /> Adjust your photo
        </DialogTitle>
        <p className="text-white/40 text-xs mb-4">Drag to reposition · scroll or slider to zoom</p>

        {imageSrc && (
          <div className="flex flex-col items-center">
            <div
              className="relative rounded-3xl overflow-hidden bg-black/40 select-none cursor-move touch-none"
              style={{ width: CANVAS_SIZE, height: CANVAS_SIZE }}
              onMouseDown={onMouseDown}
              onTouchStart={onMouseDown}
              onWheel={(e) => { e.preventDefault(); zoom(e.deltaY > 0 ? -0.05 : 0.05); }}
              data-testid="crop-canvas"
            >
              {/* The image */}
              <img
                ref={imageRef}
                src={imageSrc}
                alt="crop-source"
                onLoad={onImgLoad}
                draggable={false}
                className="absolute pointer-events-none max-w-none"
                style={{
                  left: CANVAS_SIZE / 2,
                  top: CANVAS_SIZE / 2,
                  width: imgSize.w * scale,
                  height: imgSize.h * scale,
                  transform: `translate(calc(-50% + ${pos.x}px), calc(-50% + ${pos.y}px)) rotate(${rotation}deg)`,
                }}
              />
              {/* Circular mask */}
              <div className="absolute inset-0 pointer-events-none"
                style={{
                  boxShadow: '0 0 0 9999px rgba(5, 5, 10, 0.75)',
                  clipPath: 'circle(50% at 50% 50%)',
                  WebkitClipPath: 'circle(50% at 50% 50%)',
                  background: 'transparent',
                }} />
              {/* Circle outline overlay */}
              <div className="absolute inset-0 rounded-full border-2 border-[#00E5FF]/60 pointer-events-none"
                style={{
                  boxShadow: '0 0 40px rgba(0,229,255,0.15), inset 0 0 0 3px rgba(0,0,0,0.6)',
                }} />
              {/* Drag hint */}
              <div className="absolute top-3 left-3 flex items-center gap-1 px-2 py-1 rounded-lg bg-black/50 backdrop-blur-sm border border-white/10 text-white/60 text-[10px] pointer-events-none">
                <Move className="w-3 h-3" /> drag
              </div>
            </div>

            {/* Zoom slider */}
            <div className="w-full flex items-center gap-3 mt-5">
              <button onClick={() => zoom(-0.1)} className="p-2 rounded-lg hover:bg-white/[0.05] text-white/60 hover:text-white"
                data-testid="zoom-out-btn">
                <ZoomOut className="w-4 h-4" />
              </button>
              <input type="range" min="0.2" max="5" step="0.01" value={scale}
                onChange={(e) => setScale(parseFloat(e.target.value))} data-testid="zoom-slider"
                className="flex-1 h-1.5 rounded-full bg-white/[0.06] appearance-none accent-[#00E5FF]" />
              <button onClick={() => zoom(0.1)} className="p-2 rounded-lg hover:bg-white/[0.05] text-white/60 hover:text-white"
                data-testid="zoom-in-btn">
                <ZoomIn className="w-4 h-4" />
              </button>
              <button onClick={() => setRotation((r) => (r + 90) % 360)} className="p-2 rounded-lg hover:bg-white/[0.05] text-white/60 hover:text-white"
                data-testid="rotate-btn" title="Rotate 90°">
                <RotateCw className="w-4 h-4" />
              </button>
            </div>

            <div className="flex gap-2 mt-6 w-full">
              <Button onClick={onClose} variant="outline" className="flex-1 border-white/10 text-white/60 rounded-xl" data-testid="crop-cancel-btn">
                Cancel
              </Button>
              <Button onClick={doCrop} disabled={uploading} data-testid="crop-save-btn"
                className="flex-1 bg-gradient-to-r from-[#00E5FF] to-[#8A2BE2] text-black hover:opacity-90 rounded-xl">
                <Save className="w-3.5 h-3.5 mr-2" />{uploading ? 'Uploading…' : 'Set photo'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AvatarCropModal;
